import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useMedia } from "@/contexts/MediaContext";
import Button from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps, Flows } from "@/types/flows";
import { VideoTrimmer } from "@/components/file/VideoTrimmer";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { HomeFileInputArea } from "./HomeFileInputArea";
import { videoProcessProgress, videoUploadProgress } from "@utils/uploadProgressUtils";
import { useFlowState } from "@/hooks/useFlowState";
import { useModel } from "@/contexts/ModelContext";
import { MIN_INFERENCES_SAMPLE } from "@/constants/inference";
import { VideoModal } from "./VideoModal";
import { ExampleVideos } from "./ExampleVideos";
import { MediaItemStatus } from "@/types/media";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import { useMediaStore } from "@/store";
import { CreditBanner } from "@/components/layout/CreditPromoComponents/CreditBanner";
import { useFlow } from "@/contexts/FlowContext";
import { useMobile } from "@/hooks/useMobile";

const createStaggerAnimation = (delay: number) => ({
    initial: { opacity: 0, y: -20, filter: "blur(10px)" },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    exit: { opacity: 0, y: -20, filter: "blur(10px)" },
    transition: { duration: 0.3, ease: "easeOut", delay }
});

export const FileInput = memo(() => {
    const { updateMediaItem } = useMediaUtils();
    const { mediaItemToTrim, mediaItems, erroredMediaItems, addMediaItems, orderedMediaItems } =
        useMedia();
    const { setStep } = useFlowState();
    const { track } = useAnalytics();
    const { setTextPromptStr } = useModel();
    const { setCurrentMedia } = useMediaUtils();
    const media = useMediaStore();
    const progressIntervalRef = React.useRef<number | null>(null);
    const isMobile = useMobile();
    const [copied, setCopied] = useState(false);

    const { setFlowLocked } = useFlow();

    const handleCopyLink = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy link:", err);
        }
    }, []);

    const advanceToPrompt = useCallback(
        (textPrompts?: string) => {
            if (textPrompts) {
                setTextPromptStr(textPrompts);
            }

            // Set flowLocked to true before navigation to ensure loading state appears immediately
            setFlowLocked(true);

            // Set first file as currentMedia when navigating to PROMPT step
            // This ensures the first file is selected when coming from UPLOAD step
            if (orderedMediaItems.length > 0) {
                const firstFile = orderedMediaItems[0];
                const frame = firstFile.isVideo
                    ? media[firstFile.segmentToFrameMediaItemIdMap?.[0]!]
                    : firstFile;

                if (frame) {
                    setCurrentMedia({ file: firstFile, frame: frame, frameSegment: 0 });
                }
            }

            setStep({ flow: Flows.BUILD, step: BuildSteps.PROMPT });
        },
        [setStep, setTextPromptStr, orderedMediaItems, setCurrentMedia, media, setFlowLocked]
    );

    // Enhanced progress tracking for video files with smooth animation

    const videoUploads = useMemo(() => {
        return mediaItems.filter((mediaItem) => mediaItem.isVideo && mediaItem.startTime);
    }, [mediaItems]);

    const isUploadingVideos = useMemo(() => {
        return videoUploads.length > 0;
    }, [videoUploads]);

    const updateUploadProgress = useCallback(() => {
        mediaItems?.forEach((mediaItem) => {
            if (mediaItem.isVideo && mediaItem.startTime) {
                const elapsed = Date.now() - mediaItem.startTime;
                let updatedProgress: number | undefined = undefined;

                if (mediaItem.status === MediaItemStatus.UPLOADING) {
                    const newProgress = videoUploadProgress(mediaItem, { elapsed });

                    if (Math.abs(newProgress - (mediaItem.progress || 0)) >= 0.5) {
                        updatedProgress = Math.round(newProgress);
                    }
                } else if (mediaItem.status === MediaItemStatus.PROCESSING) {
                    const currentProgress = mediaItem.progress || 50;
                    const newProgress = videoProcessProgress(mediaItem, {
                        elapsed,
                        currentProgress
                    });

                    if (Math.abs(newProgress - currentProgress) >= 0.5) {
                        updatedProgress = Math.round(newProgress);
                    }
                }

                if (updatedProgress !== undefined) {
                    updateMediaItem({ ...mediaItem, progress: updatedProgress });
                }
            }
            return mediaItem;
        });
    }, [mediaItems, updateMediaItem]);

    const resetProgress = useCallback(() => {
        if (!progressIntervalRef.current) return;

        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
    }, [progressIntervalRef]);

    useEffect(() => {
        if (isUploadingVideos) {
            // Update progress every 200ms for smoother animation
            progressIntervalRef.current = window.setInterval(updateUploadProgress, 200);
        } else {
            resetProgress();
        }

        return () => {
            resetProgress();
        };
    }, [isUploadingVideos, resetProgress, updateUploadProgress]);

    const onSubmit = useCallback(
        (textPrompts: string) => {
            track({
                event: AnalyticsFlowEvent.FIND_MY_OBJECTS_CLICKED,
                data: {
                    textPrompts: textPrompts
                        .split(",")
                        .map((s) => s.trim())
                        .filter((s) => s),
                    fileCount: mediaItems.length
                }
            });
            advanceToPrompt(textPrompts);
        },
        [advanceToPrompt, track, mediaItems.length]
    );

    const handleSetExamplePrompt = useCallback(
        (prompt: string) => {
            setTextPromptStr(prompt);
        },
        [setTextPromptStr]
    );

    const QR_SIZE = 90;

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                className="home"
            >
                <CreditBanner />

                <div className="homeContent">
                    <motion.div className="homeHeader" {...createStaggerAnimation(0)}>
                        <h1 className="homeTitle">
                            Build a <span className="homeGradientText">Computer Vision Model</span>{" "}
                            in Minutes
                        </h1>
                        <p className="homeSubtitle">
                            All you need is a short video or {MIN_INFERENCES_SAMPLE} images
                        </p>
                    </motion.div>

                    <motion.div
                        {...createStaggerAnimation(0.2)}
                        className="flex w-full flex-col items-center justify-center gap-4"
                    >
                        <HomeFileInputArea
                            addMediaItems={addMediaItems}
                            erroredMediaItems={erroredMediaItems}
                            qrSize={QR_SIZE}
                            showAllMethods={false}
                            onSubmit={onSubmit}
                        />
                        {isMobile && (
                            <>
                                <Button onClick={handleCopyLink} type="tertiary" size="large">
                                    <i
                                        className={twClassNames(
                                            "far",
                                            copied ? "fa-check" : "fa-link"
                                        )}
                                    />
                                    {copied ? "Link copied!" : "Try it now on your computer"}
                                </Button>
                                <p className="text-sm text-tertiary">
                                    Full mobile support is coming soon!
                                </p>
                            </>
                        )}
                    </motion.div>
                </div>

                <ExampleVideos
                    isVideoTrimActive={mediaItemToTrim?.validation?.needsTrimming}
                    onAddMediaItems={addMediaItems}
                    onSetTextPrompt={handleSetExamplePrompt}
                />
            </motion.div>

            {/* Video Trimmer Modal */}
            {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}
        </>
    );
});

FileInput.displayName = "FileInput";
